import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { abrirBoleto } from '../../utils/impressaoVenda';
import s from '../../pages/PedidosOrcamentos.module.css';
import InputDataFiltro from '../common/InputDataFiltro';

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtD     = d => d ? new Date(String(d).slice(0, 10) + 'T12:00').toLocaleDateString('pt-BR') : '—';
const hojeISO  = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const BADGE = {
  emitido:        { bg: '#e5e7eb', fg: '#374151', l: 'Emitido' },
  remessa_gerada: { bg: '#dbeafe', fg: '#1e40af', l: 'Remessa gerada' },
  registrado:     { bg: '#fef3c7', fg: '#92400e', l: 'Registrado' },
  liquidado:      { bg: '#dcfce7', fg: '#166534', l: 'Liquidado' },
  baixado:        { bg: '#fee2e2', fg: '#b91c1c', l: 'Baixado' },
};

export default function ModalBoletos({ onClose }) {
  const [aba, setAba]         = useState('gerar');
  const [contas, setContas]   = useState([]);       // contas bancárias
  const [contaSel, setContaSel] = useState('');
  const [abertas, setAbertas] = useState([]);       // contas a receber em aberto
  const [marcadas, setMarcadas] = useState({});
  const [boletos, setBoletos]  = useState([]);
  const [emissaoDe, setEmissaoDe]   = useState(hojeISO());
  const [emissaoAte, setEmissaoAte] = useState(hojeISO());
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro]       = useState('');
  const [sucesso, setSucesso] = useState('');
  const retornoRef = useRef(null);

  useEffect(() => {
    api.get('/financeiro/contas').then(({ data }) => {
      const bancarias = (data || []).filter(c => c.ativo && c.tipo === 'banco');
      setContas(bancarias);
      if (bancarias.length === 1) setContaSel(String(bancarias[0].id));
    }).catch(() => {});
  }, []);

  const carregarAbertas = useCallback(async () => {
    try {
      const { data } = await api.get('/financeiro/contas-receber', { params: { status: 'aberto' } });
      const lista = Array.isArray(data) ? data : (data.contas || data.rows || []);
      setAbertas(lista.filter(c => !c.baixa_id));
    } catch { setAbertas([]); }
  }, []);

  const carregarBoletos = useCallback(async () => {
    try {
      const { data } = await api.get('/financeiro/boletos', {
        params: { emissao_de: emissaoDe || undefined, emissao_ate: emissaoAte || undefined },
      });
      setBoletos(data);
    } catch { setBoletos([]); }
  }, [emissaoDe, emissaoAte]);

  useEffect(() => { carregarAbertas(); carregarBoletos(); }, [carregarAbertas, carregarBoletos]);

  const idsMarcados = Object.keys(marcadas).filter(k => marcadas[k]).map(Number);

  const gerar = async () => {
    if (!contaSel) { setErro('Selecione a conta bancária.'); return; }
    if (!idsMarcados.length) { setErro('Marque ao menos uma conta a receber.'); return; }
    setOcupado(true); setErro(''); setSucesso('');
    try {
      const { data } = await api.post('/financeiro/boletos/gerar', {
        conta_financeira_id: parseInt(contaSel), contas_receber_ids: idsMarcados,
      });
      setSucesso(`${data.gerados} boleto(s) gerado(s).`);
      setMarcadas({});
      await Promise.all([carregarAbertas(), carregarBoletos()]);
      setAba('boletos');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar boletos.');
    } finally { setOcupado(false); }
  };

  const gerarRemessa = async () => {
    if (!contaSel) { setErro('Selecione a conta bancária.'); return; }
    setOcupado(true); setErro(''); setSucesso('');
    try {
      const resp = await api.post('/financeiro/boletos/remessa',
        { conta_financeira_id: parseInt(contaSel) }, { responseType: 'blob' });
      const nome = (resp.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1] || 'REMESSA.REM';
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
      setSucesso(`Remessa ${nome} gerada e baixada — envie ao banco.`);
      await carregarBoletos();
    } catch (err) {
      let msg = 'Erro ao gerar remessa.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).erro || msg; } catch (_) {}
      } else msg = err.response?.data?.erro || msg;
      setErro(msg);
    } finally { setOcupado(false); }
  };

  const importarRetorno = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcupado(true); setErro(''); setSucesso('');
    try {
      const conteudo = await file.text();
      const { data } = await api.post('/financeiro/boletos/retorno',
        { conteudo, arquivo_nome: file.name });
      setSucesso(`Retorno processado: ${data.liquidados} liquidado(s), ${data.registrados} registrado(s), ${data.baixados} baixado(s) de ${data.registros} registro(s).`);
      await Promise.all([carregarAbertas(), carregarBoletos()]);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao processar retorno.');
    } finally {
      setOcupado(false);
      if (retornoRef.current) retornoRef.current.value = '';
    }
  };

  return (
    <div className={s.overlay} onMouseDown={e => e.target === e.currentTarget && !ocupado && onClose()}>
      <div className={s.modal} style={{ width: 860, maxWidth: '97vw' }}>
        <div className={s.modalTitle}>🏦 Boletos Bancários — CNAB400</div>
        <div className={s.modalBody} style={{ gap: 8 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, minWidth: 240 }}>
              Conta bancária (cedente)
              <select className={s.selectErp} value={contaSel} onChange={e => setContaSel(e.target.value)}>
                <option value="">— Selecione —</option>
                {contas.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nome}{c.banco_codigo ? ` (${c.banco_codigo})` : ' — sem cód. banco'}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid #c8d6e0', flex: 1 }}>
              {[['gerar', 'Gerar boletos'], ['boletos', 'Boletos / Remessa / Retorno']].map(([k, l]) => (
                <button key={k} onClick={() => setAba(k)}
                  style={{ padding: '4px 14px', fontSize: 12, cursor: 'pointer', border: 'none', background: 'none',
                    borderBottom: aba === k ? '2px solid #2563eb' : '2px solid transparent',
                    color: aba === k ? '#2563eb' : '#555', fontWeight: aba === k ? 'bold' : 'normal', marginBottom: -2 }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {erro    && <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#b91c1c' }}>{erro}</div>}
          {sucesso && <div style={{ background: '#ecfdf5', border: '1px solid #10b981', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: '#065f46' }}>{sucesso}</div>}

          {aba === 'gerar' && (<>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className={s.itensGrid}>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>
                      <input type="checkbox"
                        checked={abertas.length > 0 && idsMarcados.length === abertas.length}
                        onChange={e => {
                          const m = {};
                          if (e.target.checked) abertas.forEach(c => { m[c.id] = true; });
                          setMarcadas(m);
                        }} />
                    </th>
                    <th>Descrição</th>
                    <th>Cliente</th>
                    <th style={{ width: 95 }}>Vencimento</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {abertas.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 14 }}>
                      Nenhuma conta a receber em aberto.
                    </td></tr>
                  )}
                  {abertas.map(c => (
                    <tr key={c.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={!!marcadas[c.id]}
                          onChange={e => setMarcadas(m => ({ ...m, [c.id]: e.target.checked }))} />
                      </td>
                      <td>{c.descricao}</td>
                      <td>{c.cliente_nome || '—'}</td>
                      <td>{fmtD(c.data_vencimento)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoeda(c.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={ocupado} onClick={gerar}>
                {ocupado ? 'Gerando…' : `🏦 Gerar ${idsMarcados.length || ''} boleto(s)`}
              </button>
            </div>
          </>)}

          {aba === 'boletos' && (<>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
                Emissão de
                <InputDataFiltro className={s.inputErp} value={emissaoDe}
                  onChange={e => setEmissaoDe(e.target.value)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
                até
                <InputDataFiltro className={s.inputErp} value={emissaoAte}
                  onChange={e => setEmissaoAte(e.target.value)} />
              </label>
              <button className={s.btnErp} disabled={ocupado}
                title="Mostra os boletos de todas as datas"
                onClick={() => { setEmissaoDe(''); setEmissaoAte(''); }}>
                Todos
              </button>
              <button className={s.btnErp} disabled={ocupado}
                title="Volta a mostrar só os boletos emitidos hoje"
                onClick={() => { setEmissaoDe(hojeISO()); setEmissaoAte(hojeISO()); }}>
                Hoje
              </button>
              <div style={{ flex: 1 }} />
              <button className={s.btnErp} disabled={ocupado} onClick={gerarRemessa}
                title="Gera o arquivo .REM com os boletos emitidos da conta selecionada">
                📄 Gerar remessa (.REM)
              </button>
              <button className={s.btnErp} disabled={ocupado} onClick={() => retornoRef.current?.click()}
                title="Importa o arquivo de retorno do banco — liquidações baixam o contas a receber">
                📥 Importar retorno (.RET)
              </button>
              <input ref={retornoRef} type="file" accept=".ret,.txt,.RET,.TXT" style={{ display: 'none' }}
                onChange={importarRetorno} />
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className={s.itensGrid}>
                <thead>
                  <tr>
                    <th style={{ width: 45 }}>#</th>
                    <th>Conta a receber</th>
                    <th>Cliente</th>
                    <th style={{ width: 105 }}>Nosso nº</th>
                    <th style={{ width: 85 }}>Emissão</th>
                    <th style={{ width: 90 }}>Vencimento</th>
                    <th style={{ width: 95, textAlign: 'right' }}>Valor</th>
                    <th style={{ width: 110 }}>Status</th>
                    <th style={{ width: 55 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {boletos.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign: 'center', color: '#888', padding: 14 }}>
                      Nenhum boleto no período — use “Todos” para ver todas as datas.
                    </td></tr>
                  )}
                  {boletos.map(b => {
                    const bd = b.baixa_pendente
                      ? { bg: '#fef3c7', fg: '#b91c1c', l: 'Baixa a enviar' }
                      : (BADGE[b.status] || BADGE.emitido);
                    return (
                      <tr key={b.id}>
                        <td>{b.id}</td>
                        <td>{b.conta_descricao}</td>
                        <td>{b.cliente_nome || '—'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{b.nosso_numero}{b.nosso_numero_dv ? `-${b.nosso_numero_dv}` : ''}</td>
                        <td>{fmtD(b.criado_em)}</td>
                        <td>{fmtD(b.vencimento)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(b.valor)}</td>
                        <td>
                          <span style={{ background: bd.bg, color: bd.fg, fontSize: 10, fontWeight: 'bold',
                            padding: '2px 6px', borderRadius: 4 }}
                            title={b.data_credito ? `Crédito em ${fmtD(b.data_credito)} — ${fmtMoeda(b.valor_pago)}` : ''}>
                            {bd.l}
                          </span>
                        </td>
                        <td>
                          <button className={s.btnErp} style={{ height: 20, fontSize: 11, padding: '0 6px' }}
                            title={b.origem === 'cora' ? 'Abrir boleto (página do Cora)' : 'Imprimir boleto (PDF)'}
                            onClick={() => abrirBoleto(b)}>🖨️</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>)}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => onClose()} disabled={ocupado}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
